import { type PayloadAction, createSlice } from "@reduxjs/toolkit"

export interface Message<T> {
  type: string
  payload?: T
}

export enum AlertLevel {
  INFO = "INFO",
  WARN = "WARN",
  ERROR = "ERROR",
  SUCCESS = "SUCCESS",
}

export interface Alert {
  id: string
  level: AlertLevel
  title: string
  description?: string
  options?: AlertOption[]
}

export interface AlertOption {
  label: string
  onClick: () => void
}

export interface AlertsAppState {
  alerts: Alert[]
}

function alertId(): string {
  return Math.random().toString(36).substring(2, 9)
}

const initialState: AlertsAppState = {
  alerts: [],
}

export type AlertAction = PayloadAction<Alert>
export type AlertDismissAction = PayloadAction<string>

const alertsSlice = createSlice({
  name: "alerts",
  initialState,
  reducers: {
    pushAlert(state, action: AlertAction) {
      state.alerts.push(action.payload)
    },
    dismissAlert(state, action: AlertDismissAction) {
      const alertId = action.payload
      state.alerts = state.alerts.filter((alert) => alert.id !== alertId)
    },
  },
})

export const { dismissAlert } = alertsSlice.actions
export const AlertReducer = alertsSlice.reducer

const pushAlert = (level: AlertLevel, title: string) =>
  alertsSlice.actions.pushAlert({
    id: alertId(),
    level,
    title,
  })
export const pushSuccessAlert = (title: string): AlertAction =>
  pushAlert(AlertLevel.SUCCESS, title)
export const pushInfoAlert = (title: string): AlertAction =>
  pushAlert(AlertLevel.INFO, title)
export const pushWarnAlert = (title: string): AlertAction =>
  pushAlert(AlertLevel.WARN, title)
export const pushErrorAlert = (title: string): AlertAction =>
  pushAlert(AlertLevel.ERROR, title)
